import { useMemo } from "react";

import {
  SIGNAL_PREVIEW_CATEGORY,
  SignalPreviewCountsByKey,
  SignalPreviewItemInput,
  useSignalPreviewCounts,
} from "lib/hooks/api/signals/useSignalPreviewCounts";

export type FeedPreviewCountsInput = {
  keywords: string[];
  competitorIds: string[];
  partnerIds: string[];
  cpvCodes: string[];
  countries: string[];
};

export type FeedPreviewCounts = {
  keywordCounts: SignalPreviewCountsByKey;
  competitorCounts: SignalPreviewCountsByKey;
  partnerCounts: SignalPreviewCountsByKey;
  cpvCounts: SignalPreviewCountsByKey;
  isLoadingCounts: boolean;
};

// Request keys are namespaced by category so the count the backend echoes back
// for one category can't overwrite another's — e.g. a numeric keyword "34000000"
// and CPV code "34000000". The prefix is stripped again when splitting the
// response into per-category maps keyed by the raw value each input looks up by.
const KEY_PREFIX = {
  keyword: "keyword:",
  competitor: "competitor:",
  partner: "partner:",
  cpv: "cpv:",
} as const;

/**
 * Fetches feed-preview match counts for the signal-settings inputs, returning a
 * separate map per category so each input looks up by its own raw value
 * (keyword string / org id / CPV code) with no cross-category collisions.
 */
export function useFeedPreviewCounts({
  keywords,
  competitorIds,
  partnerIds,
  cpvCodes,
  countries,
}: FeedPreviewCountsInput): FeedPreviewCounts {
  // A fresh array each render is fine — react-query hashes the query key by value.
  const signals: SignalPreviewItemInput[] = [
    ...keywords.map((value) => ({
      key: `${KEY_PREFIX.keyword}${value}`,
      category: SIGNAL_PREVIEW_CATEGORY.KEYWORD,
      value,
    })),
    ...competitorIds.map((value) => ({
      key: `${KEY_PREFIX.competitor}${value}`,
      category: SIGNAL_PREVIEW_CATEGORY.COMPETITOR,
      value,
    })),
    ...partnerIds.map((value) => ({
      key: `${KEY_PREFIX.partner}${value}`,
      category: SIGNAL_PREVIEW_CATEGORY.PARTNER,
      value,
    })),
    ...cpvCodes.map((value) => ({
      key: `${KEY_PREFIX.cpv}${value}`,
      category: SIGNAL_PREVIEW_CATEGORY.CPV_CODE,
      value,
    })),
  ];

  const { countsByKey, isFetching } = useSignalPreviewCounts(signals, { countries });

  const counts = useMemo(() => {
    const pick = (prefix: string): SignalPreviewCountsByKey => {
      const map: SignalPreviewCountsByKey = {};
      for (const [key, count] of Object.entries(countsByKey)) {
        if (key.startsWith(prefix)) {
          map[key.slice(prefix.length)] = count;
        }
      }
      return map;
    };
    return {
      keywordCounts: pick(KEY_PREFIX.keyword),
      competitorCounts: pick(KEY_PREFIX.competitor),
      partnerCounts: pick(KEY_PREFIX.partner),
      cpvCounts: pick(KEY_PREFIX.cpv),
    };
  }, [countsByKey]);

  return { ...counts, isLoadingCounts: isFetching };
}
